import type { ContractVariantSpec } from '../api/types';

export type ContractDesignMode = 'leder' | 'pass' | 'klassisch' | 'beta';

export const CONTRACT_DESIGN_MODES: { key: ContractDesignMode; label: string }[] = [
  { key: 'leder', label: 'Leder (Dunkelblau / Gold)' },
  { key: 'pass', label: 'Der Pass (§-Spalten & Kompaktkarte)' },
  { key: 'klassisch', label: 'Klassisch (Hell mit Goldlinie)' },
  { key: 'beta', label: 'BETA Project & Immobilien (Exklusives Vermarktungsmandat)' },
];

export const OBJECT_TYPES = [
  'Eigentumswohnung',
  'Einfamilienhaus',
  'Doppelhaushälfte',
  'Reihenhaus',
  'Mehrfamilienhaus',
  'Grundstück',
  'Gewerbeobjekt',
] as const;

export const CONSUMER_OBJECT_TYPES = new Set<string>([
  'Eigentumswohnung',
  'Einfamilienhaus',
  'Doppelhaushälfte',
  'Reihenhaus',
]);

export const CONTRACT_TEMPLATES: { key: ContractVariantSpec['template_key']; label: string; short: string }[] = [
  { key: 'maklervertrag_alleinauftrag', label: 'Einfacher Alleinauftrag (Standard)', short: 'Alleinauftrag' },
  { key: 'maklervertrag_einfach', label: 'Einfacher Maklerauftrag (ohne Exklusivität)', short: 'Einfacher Auftrag' },
  { key: 'maklervertrag_qualifiziert', label: 'Qualifizierter Alleinauftrag (Individualvereinbarung)', short: 'Qualifiziert' },
];

export const COMMISSION_MODELS = [
  { value: 'Halbteilung', label: 'Halbteilung (je Seite gleich)' },
  { value: 'Verkäufer mit Weitergabe an den Käufer', label: 'Weitergabe an Käufer (z. B. 0 % / 7,14 %)' },
  { value: 'Nur Verkäufer (Innenprovision)', label: 'Nur Verkäufer (Innenprovision)' },
] as const;

export const COMMISSION_PRESETS = ['0', '1,79', '2,38', '2,98', '3,57', '4,76', '5,95', '7,14'] as const;
export const TERM_PRESETS = ['3', '6', '9', '12'] as const;

export function parsePercentDe(val: string | undefined): number {
  if (!val) return 0;
  const n = Number.parseFloat(val.replace(/\./g, '').replace(',', '.').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function formatPercentDe(num: number): string {
  if (!Number.isFinite(num) || num <= 0) return '0';
  const rounded = Math.round(num * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace('.', ',').replace(/0$/, '');
}

export function defaultContractVariant(objectType = 'Einfamilienhaus', label = 'Variante A (Empfehlung)', price = ''): ContractVariantSpec {
  const isConsumer = CONSUMER_OBJECT_TYPES.has(objectType);
  return {
    label,
    template_key: 'maklervertrag_alleinauftrag',
    object_type: objectType,
    price,
    commission_model: isConsumer ? 'Halbteilung' : 'Verkäufer mit Weitergabe an den Käufer',
    commission_seller: isConsumer ? '3,57' : '0',
    commission_buyer: isConsumer ? '3,57' : '7,14',
    term_months: '6',
    special_agreements: '',
    individual_agreement: '',
    legal_override: false,
    legal_override_reason: '',
  };
}

export function fallbackContractVariant(base: ContractVariantSpec, idx: number): ContractVariantSpec {
  const letter = String.fromCharCode(65 + idx);
  const isConsumer = CONSUMER_OBJECT_TYPES.has(base.object_type ?? '');
  if (!isConsumer && idx === 1) {
    return {
      ...base,
      label: `Variante ${letter} (Halbteilung 3,57 % / 3,57 %)`,
      commission_model: 'Halbteilung',
      commission_seller: '3,57',
      commission_buyer: '3,57',
      legal_override: false,
    };
  }
  if (idx === 1) {
    return {
      ...base,
      label: `Variante ${letter} (Kürzere Laufzeit 3 Monate)`,
      term_months: '3',
      legal_override: false,
    };
  }
  return {
    ...base,
    label: `Variante ${letter} (Einfacher Auftrag ohne Bindung)`,
    template_key: 'maklervertrag_einfach',
    legal_override: false,
  };
}

export function checkContractGuardrails(v: ContractVariantSpec): string[] {
  const warnings: string[] = [];
  const seller = parsePercentDe(v.commission_seller);
  const buyer = parsePercentDe(v.commission_buyer);
  const obj = (v.object_type ?? '').trim();
  if (CONSUMER_OBJECT_TYPES.has(obj)) {
    if (v.commission_model === 'Verkäufer mit Weitergabe an den Käufer' && buyer > seller + 0.001) {
      warnings.push(
        `§ 656d BGB (Halbteilungsgrundsatz): Bei ${obj} muss der Verkäufer mindestens die Hälfte der Gesamtprovision tragen (aktuell Verkäufer ${v.commission_seller || '0'} % / Käufer ${v.commission_buyer || '0'} %).`,
      );
    } else if (v.commission_model === 'Halbteilung' && Math.abs(seller - buyer) > 0.005) {
      warnings.push(
        `§ 656c BGB (Halbteilung): Bei ${obj} müssen Verkäufer- und Käuferprovision gleich hoch sein (aktuell ${v.commission_seller || '0'} % / ${v.commission_buyer || '0'} %).`,
      );
    }
  }
  if (v.template_key === 'maklervertrag_qualifiziert' && !(v.individual_agreement ?? '').trim()) {
    warnings.push(
      'BGH-Rechtsprechung: Ein qualifizierter Alleinauftrag (Verweisungs- und Eigengeschäftsklausel) ist nur wirksam, wenn er im Einzelnen ausgehandelt wurde.',
    );
  }
  return warnings;
}
